import { useEffect, useRef } from 'react';
import { useTimer, REST_PRESETS } from '@/contexts/TimerContext';

export default function FullscreenTimer() {
  const { isOpen, isRunning, isFinished, timeLeft, totalTime, progress, closeTimer, addTime, pauseTimer, resumeTimer, startTimer } = useTimer();
  const waveContainerRef = useRef<HTMLDivElement>(null);
  const lastWaveTimeRef = useRef<number>(0);

  // Wave animation every 3 seconds
  useEffect(() => {
    if (!isRunning || isFinished) return;

    const checkWave = () => {
      const now = Date.now();
      if (now - lastWaveTimeRef.current >= 3000) {
        lastWaveTimeRef.current = now;
        triggerWave();
      }
    };

    const interval = setInterval(checkWave, 100);
    return () => clearInterval(interval);
  }, [isRunning, isFinished]);

  const triggerWave = () => {
    if (!waveContainerRef.current) return;
    
    const wave = document.createElement('div');
    wave.className = 'timer-wave';
    waveContainerRef.current.appendChild(wave);
    
    // Remove after animation completes
    setTimeout(() => {
      wave.remove();
    }, 2000);
  };

  if (!isOpen || (!isRunning && !isFinished && timeLeft === totalTime)) {
    return null;
  }

  // Circle progress calculation
  const radius = 120;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (progress / 100);

  return (
    <div className="fixed inset-0 bg-gray-900/95 z-[9998] flex flex-col items-center justify-center">
      {/* Timer circle with waves container */}
      <div className="relative flex items-center justify-center">
        {/* Wave container - positioned at center of timer circle */}
        <div ref={waveContainerRef} className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-visible" style={{ width: '280px', height: '280px' }}>
          {/* Waves will be added dynamically */}
        </div>

        {/* Timer circle */}
        <div className="relative">
          <svg width="280" height="280" className="transform -rotate-90">
            {/* Background circle */}
            <circle
              cx="140"
              cy="140"
              r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="8"
            />
            {/* Progress circle */}
            <circle
              cx="140"
              cy="140"
              r={radius}
              fill="none"
              stroke={isFinished ? '#22c55e' : '#0ea5e9'}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-200"
            />
          </svg>

          {/* Seconds display */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-8xl font-bold ${isFinished ? 'text-green-400' : 'text-white'}`}>
              {timeLeft}
            </span>
          </div>
        </div>
      </div>

      {/* Status text */}
      <div className="mt-8 text-center">
        {isFinished ? (
          <p className="text-green-400 text-xl font-medium">Отдых завершён</p>
        ) : (
          <p className="text-gray-400 text-lg">
            {isRunning ? 'Отдыхай...' : 'Пауза'}
          </p>
        )}
      </div>

      {/* Controls */}
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {isFinished ? (
          <>
            {/* Quick restart buttons */}
            {REST_PRESETS.slice(0, 3).map((preset) => (
              <button
                key={preset.seconds}
                onClick={() => startTimer(preset.seconds)}
                className="px-5 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-colors"
              >
                {preset.label}
              </button>
            ))}
            <button
              onClick={closeTimer}
              className="px-8 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors"
            >
              Закрыть
            </button>
          </>
        ) : (
          <>
            {/* Pause/Resume */}
            <button
              onClick={isRunning ? pauseTimer : resumeTimer}
              className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              {isRunning ? (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>

            {/* Add 30s */}
            <button
              onClick={() => addTime(30)}
              className="px-6 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-colors"
            >
              +30с
            </button>

            {/* Close/Skip */}
            <button
              onClick={closeTimer}
              className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Wave animation styles */}
      <style>{`
        .timer-wave {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 280px;
          height: 280px;
          margin-left: -140px;
          margin-top: -140px;
          border-radius: 50%;
          border: 2px solid rgba(14, 165, 233, 0.6);
          animation: wave-expand 2s ease-out forwards;
        }
        
        @keyframes wave-expand {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
